import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/Students.css";

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return;

    try {
      setLoading(true);

      // Fetch all students (admin endpoint)
      const response = await axios.get("/api/students/all", {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      setStudents(response.data);
    } catch (error) {
      console.error("❌ Error fetching students:", error);
      if (error.response?.status === 403) {
        alert("⛔ ليس لديك صلاحية للوصول لهذه الصفحة. هذه الصفحة مخصصة للأدمن فقط.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    if (!window.confirm(`هل أنت متأكد من حذف الطالب: ${studentName}؟\nسيتم حذف جميع تسجيلاته أيضاً.`)) {
      return;
    }

    const authToken = localStorage.getItem("authToken");
    try {
      await axios.delete(`/api/students/${studentId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      alert("✅ تم حذف الطالب بنجاح");
      fetchStudents(); // Refresh list
    } catch (error) {
      console.error("❌ Error deleting student:", error);
      alert("❌ فشل حذف الطالب");
    }
  };

  if (loading) {
    return (
      <div className="students-container">
        <h1>⏳ جاري التحميل...</h1>
      </div>
    );
  }

  return (
    <div className="dashboard-container fade-in">
      <div className="welcome-banner">
        <h1>👩‍🎓 إدارة الطلاب</h1>
        <p>إدارة جميع الطلاب المسجلين في المنصة</p>
      </div>

      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-info">
            <h3>إجمالي الطلاب</h3>
            <p className="stat-value">{students.length}</p>
          </div>
          <div className="stat-icon">👥</div>
        </div>
        <div className="stat-card success">
          <div className="stat-info">
            <h3>تسجيلات مقبولة</h3>
            <p className="stat-value">
              {students.reduce((acc, curr) => acc + (curr.approved_courses || 0), 0)}
            </p>
          </div>
          <div className="stat-icon">✅</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-info">
            <h3>قيد المراجعة</h3>
            <p className="stat-value">
              {students.reduce((acc, curr) => acc + (curr.pending_courses || 0), 0)}
            </p>
          </div>
          <div className="stat-icon">⏳</div>
        </div>
      </div>

      {/* Students Table Card */}
      <div className="card">
        <div className="card-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0 }}>قائمة الطلاب</h2>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            عرض {students.length} طالب
          </span>
        </div>

        {students.length === 0 ? (
          <div className="empty-state">
            <p>لا يوجد طلاب مسجلين بعد</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد الإلكتروني</th>
                  <th>الدورات</th>
                  <th>الحالة</th>
                  <th>تاريخ التسجيل</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="avatar-circle">
                          {student.full_name ? student.full_name[0].toUpperCase() : '?'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600 }}>{student.full_name || "بدون اسم"}</span>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                            ID: {student.id}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{student.email}</td>
                    <td>
                      <span className="badge badge-info">{student.courses_count} دورات</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {student.approved_courses > 0 && (
                          <span className="badge badge-success">{student.approved_courses} مقبول</span>
                        )}
                        {student.pending_courses > 0 && (
                          <span className="badge badge-warning">{student.pending_courses} معلق</span>
                        )}
                        {student.courses_count === 0 && (
                          <span className="badge badge-neutral">غير مسجل</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {new Date(student.created_at).toLocaleDateString('ar-EG')}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="التفاصيل"
                          onClick={() => {
                            alert(`تفاصيل الطالب:\n\nالاسم: ${student.full_name}\nالبريد: ${student.email}\n\nالدورات:\n${student.enrollments?.map(e => `- ${e.course_title} (${e.status})`).join('\n') || 'لا يوجد'}`);
                          }}
                        >
                          👁️
                        </button>
                        <button
                          className="btn-icon delete"
                          title="حذف الحساب"
                          onClick={() => handleDeleteStudent(student.id, student.full_name)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Students;
